import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, ChevronDown, X } from 'lucide-react';
import { Transition, TransitionChild, Dialog as HeadlessDialog, DialogPanel } from '@headlessui/react';
import MarkdownRenderer from '@/components/MarkdownRenderer';

type LLMResponse = {
  llm: string;
  llmIcon?: string;
  response: string;
  mentioned: boolean;
  timestamp: string;
};

interface QueryDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  responses: LLMResponse[];
}

const QueryDetailModal: React.FC<QueryDetailModalProps> = ({
  open,
  onOpenChange,
  query,
  responses,
}) => {
  const mentionedResponses = responses.filter(r => r.mentioned);
  const notMentionedResponses = responses.filter(r => !r.mentioned);
  const [selectedResponse, setSelectedResponse] = useState<LLMResponse | null>(
    mentionedResponses[0] || notMentionedResponses[0] || null
  );
  const [showMobileLLMSelector, setShowMobileLLMSelector] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedResponse(mentionedResponses[0] || notMentionedResponses[0] || null);
      setShowMobileLLMSelector(false);
    }
  }, [open, responses]);

  const handleSelectResponse = (response: LLMResponse) => {
    setSelectedResponse(response);
    setShowMobileLLMSelector(false);
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <HeadlessDialog onClose={() => {}} className="relative z-50">
        {/* Overlay - only on desktop */}
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] hidden md:block" />
        </TransitionChild>

        {/* Modal Container */}
        <div className="fixed inset-0 z-10 md:overflow-y-auto">
          <div className="flex min-h-full md:items-center md:justify-center md:p-4">
            <TransitionChild
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 md:scale-95"
              enterTo="opacity-100 md:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 md:scale-100"
              leaveTo="opacity-0 md:scale-95"
              className="w-full h-full md:w-auto md:h-auto"
            >
              <DialogPanel className="relative w-full h-full md:w-[896px] md:max-w-[90vw] md:max-h-[90vh] md:rounded-lg border-0 md:border bg-background shadow-lg overflow-hidden">
                {/* Close button */}
                <button
                  onClick={() => onOpenChange(false)}
                  className="absolute right-3 top-3 md:right-4 md:top-4 z-10 p-2 hover:bg-secondary rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-foreground" />
                </button>

                {/* Mobile Layout */}
                <div className="flex flex-col h-full md:hidden">
                  {/* Mobile Header - Query */}
                  <div className="p-4 pt-14 border-b border-border shrink-0">
                    <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50 mb-1">
                      Query
                    </p>
                    <p className="font-sans font-medium text-sm text-foreground leading-[1.4] line-clamp-2">
                      {query}
                    </p>
                  </div>

                  {/* Mobile LLM Selector */}
                  <div className="border-b border-border shrink-0">
                    <button
                      onClick={() => setShowMobileLLMSelector(!showMobileLLMSelector)}
                      className="w-full p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {selectedResponse?.llmIcon ? (
                          <img
                            src={selectedResponse.llmIcon}
                            alt={selectedResponse.llm}
                            className="w-8 h-8 rounded-full object-contain bg-white border border-border"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                            <span className="text-xs font-medium text-foreground">
                              {selectedResponse?.llm.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-sans font-medium text-sm text-foreground text-left">
                            {selectedResponse?.llm || 'Select LLM'}
                          </p>
                          <div className={`flex items-center gap-1 text-xs ${selectedResponse?.mentioned ? 'text-[#00a944]' : 'text-[#e14944]'}`}>
                            {selectedResponse?.mentioned ? (
                              <ThumbsUp className="w-3 h-3" strokeWidth={3} />
                            ) : (
                              <ThumbsDown className="w-3 h-3" strokeWidth={3} />
                            )}
                            <span>{selectedResponse?.mentioned ? 'Mention' : 'No Mention'}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-foreground transition-transform ${showMobileLLMSelector ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mobile LLM Dropdown */}
                    {showMobileLLMSelector && (
                      <div className="border-t border-border max-h-[200px] overflow-y-auto">
                        {mentionedResponses.length > 0 && (
                          <div className="p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <ThumbsUp className="w-3 h-3 text-[#00a944]" strokeWidth={3} />
                              <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#00a944]">
                                Mention
                              </p>
                            </div>
                            <div className="space-y-1">
                              {mentionedResponses.map((response, index) => (
                                <button
                                  key={`mobile-mention-${index}`}
                                  onClick={() => handleSelectResponse(response)}
                                  className={`w-full flex items-center gap-3 p-2 rounded transition-colors text-left ${
                                    selectedResponse === response ? 'bg-secondary' : 'hover:bg-secondary/50'
                                  }`}
                                >
                                  {response.llmIcon ? (
                                    <img
                                      src={response.llmIcon}
                                      alt={response.llm}
                                      className="w-6 h-6 rounded-full object-contain bg-white border border-border"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                                      <span className="text-xs font-medium text-foreground">{response.llm.charAt(0)}</span>
                                    </div>
                                  )}
                                  <span className="font-sans text-sm text-foreground">{response.llm}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {notMentionedResponses.length > 0 && (
                          <div className="p-3 border-t border-border">
                            <div className="flex items-center gap-2 mb-2">
                              <ThumbsDown className="w-3 h-3 text-[#e14944]" strokeWidth={3} />
                              <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#e14944]">
                                No Mention
                              </p>
                            </div>
                            <div className="space-y-1">
                              {notMentionedResponses.map((response, index) => (
                                <button
                                  key={`mobile-no-mention-${index}`}
                                  onClick={() => handleSelectResponse(response)}
                                  className={`w-full flex items-center gap-3 p-2 rounded transition-colors text-left ${
                                    selectedResponse === response ? 'bg-secondary' : 'hover:bg-secondary/50'
                                  }`}
                                >
                                  {response.llmIcon ? (
                                    <img
                                      src={response.llmIcon}
                                      alt={response.llm}
                                      className="w-6 h-6 rounded-full object-contain bg-white border border-border"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                                      <span className="text-xs font-medium text-foreground">{response.llm.charAt(0)}</span>
                                    </div>
                                  )}
                                  <span className="font-sans text-sm text-foreground">{response.llm}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile Response Content */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {selectedResponse ? (
                      <>
                        {/* Query Bubble */}
                        <div className="flex justify-end mb-4">
                          <div className="bg-muted rounded-lg px-3 py-2 max-w-[85%]">
                            <p className="font-sans text-sm text-foreground">
                              {query}
                            </p>
                          </div>
                        </div>

                        {/* AI Response */}
                        <div className="min-h-[150px]">
                          {selectedResponse.response && selectedResponse.response.trim().length > 0 ? (
                            <MarkdownRenderer content={selectedResponse.response} className="text-foreground text-sm" />
                          ) : (
                            <div className="text-foreground opacity-50 italic text-sm">
                              No response received
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <p className="font-sans text-sm text-foreground opacity-50">
                          No response selected
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:flex h-[600px]">
                  {/* Left Sidebar - Query & LLM List */}
                  <div className="w-[280px] border-r border-border bg-card flex flex-col shrink-0">
                    {/* Query Header */}
                    <div className="p-6 pr-12 border-b border-border">
                      <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground opacity-50 mb-2">
                        Query
                      </p>
                      <p className="font-sans font-medium text-base text-foreground leading-[1.4]">
                        {query}
                      </p>
                    </div>

                    {/* LLM List */}
                    <div className="flex-1 overflow-y-auto">
                      {mentionedResponses.length > 0 && (
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <ThumbsUp className="w-3 h-3 text-[#00a944]" strokeWidth={3} />
                            <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#00a944]">
                              Mention
                            </p>
                          </div>
                          <div className="space-y-1">
                            {mentionedResponses.map((response, index) => (
                              <button
                                key={`mention-${index}`}
                                onClick={() => setSelectedResponse(response)}
                                className={`w-full flex items-center gap-3 p-2 rounded transition-colors text-left ${
                                  selectedResponse === response ? 'bg-secondary' : 'hover:bg-secondary/50'
                                }`}
                              >
                                {response.llmIcon ? (
                                  <img
                                    src={response.llmIcon}
                                    alt={response.llm}
                                    className="w-8 h-8 rounded-full object-contain bg-white border border-border"
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                    <span className="text-xs font-medium text-foreground">{response.llm.charAt(0)}</span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-sans font-medium text-sm text-foreground">
                                    {response.llm}
                                  </p>
                                  <p className="font-sans text-xs text-foreground opacity-50">
                                    {response.timestamp}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {notMentionedResponses.length > 0 && (
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <ThumbsDown className="w-3 h-3 text-[#e14944]" strokeWidth={3} />
                            <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#e14944]">
                              No Mention
                            </p>
                          </div>
                          <div className="space-y-1">
                            {notMentionedResponses.map((response, index) => (
                              <button
                                key={`no-mention-${index}`}
                                onClick={() => setSelectedResponse(response)}
                                className={`w-full flex items-center gap-3 p-2 rounded transition-colors text-left ${
                                  selectedResponse === response ? 'bg-secondary' : 'hover:bg-secondary/50'
                                }`}
                              >
                                {response.llmIcon ? (
                                  <img
                                    src={response.llmIcon}
                                    alt={response.llm}
                                    className="w-8 h-8 rounded-full object-contain bg-white border border-border"
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                    <span className="text-xs font-medium text-foreground">{response.llm.charAt(0)}</span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-sans font-medium text-sm text-foreground">
                                    {response.llm}
                                  </p>
                                  <p className="font-sans text-xs text-foreground opacity-50">
                                    {response.timestamp}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Content - Response Detail */}
                  <div className="flex-1 flex flex-col bg-card overflow-hidden">
                    {selectedResponse ? (
                      <>
                        {/* Response Header */}
                        <div className="px-8 pr-14 py-5 border-b border-border flex items-center justify-between">
                          <p className="font-sans text-sm text-foreground opacity-70">
                            {selectedResponse.timestamp}
                          </p>
                          <div className={`flex items-center gap-2 text-xs font-mono font-medium tracking-[0.6px] uppercase ${selectedResponse.mentioned ? 'text-[#00a944]' : 'text-[#e14944]'}`}>
                            {selectedResponse.mentioned ? (
                              <>
                                <ThumbsUp className="w-3 h-3" strokeWidth={3} />
                                <span>Mention</span>
                              </>
                            ) : (
                              <>
                                <ThumbsDown className="w-3 h-3" strokeWidth={3} />
                                <span>No Mention</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Response Content */}
                        <div className="flex-1 overflow-y-auto p-8">
                          {/* Query Bubble */}
                          <div className="flex justify-end mb-6">
                            <div className="bg-muted rounded-lg px-4 py-3 max-w-[80%]">
                              <p className="font-sans text-sm text-foreground">
                                {query}
                              </p>
                            </div>
                          </div>

                          {/* AI Response */}
                          <div className="min-h-[200px]">
                            {selectedResponse.response && selectedResponse.response.trim().length > 0 ? (
                              <MarkdownRenderer content={selectedResponse.response} className="text-foreground" />
                            ) : (
                              <div className="text-foreground opacity-50 italic">
                                No response received
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <p className="font-sans text-sm text-foreground opacity-50">
                          No response selected
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
};

export default QueryDetailModal;
